/** ms -> "5:45" or "5:45:23" (drops the hour when zero). */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '--:--'
  const total = Math.floor(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m)
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

/** seconds -> "1:05:23" for the shareable `?t=` parameter. */
export function formatTimecode(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`
}

/** "1:05:23" or "383" -> seconds. Accepts plain digits too. */
export function parseTimecode(input: string): number | null {
  const trimmed = input.trim()
  if (!trimmed) return null
  if (/^\d+$/.test(trimmed)) return Number(trimmed)
  const parts = trimmed.split(':')
  if (parts.length > 3 || parts.some((p) => p !== '' && !/^\d+$/.test(p))) return null
  return parts.reduce((acc, part) => acc * 60 + (Number(part) || 0), 0)
}

export function formatViews(views: number): string {
  if (!Number.isFinite(views) || views < 0) return ''
  if (views < 1000) return String(views)
  if (views < 1_000_000) return `${(views / 1000).toFixed(1).replace(/\.0$/, '')}K`
  return `${(views / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
}

const UNITS: Array<[limit: number, div: number, suffix: string]> = [
  [60_000, 60_000, 'm'],
  [3_600_000, 3_600_000, 'h'],
  [86_400_000, 86_400_000, 'd'],
]

export function formatRelative(ts: number, now = Date.now()): string {
  const delta = now - ts
  if (!Number.isFinite(delta)) return ''
  if (delta < 60_000) return 'just now'
  for (const [limit, div, suffix] of UNITS) {
    if (delta < limit * 60 && suffix !== 'd') return `${Math.floor(delta / div)}${suffix} ago`
  }
  if (delta < 86_400_000 * 365) return `${Math.floor(delta / 86_400_000)}d ago`
  return new Date(ts).toISOString().slice(0, 10)
}

export function formatAbsolute(ts: number): string {
  return new Date(ts).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/** Chat line timestamp, rendered in UTC to match the log's own timestamps. */
export function formatChatTime(ts: number, format: 'none' | 'hm' | 'hms'): string {
  if (format === 'none') return ''
  const d = new Date(ts)
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const mm = String(d.getUTCMinutes()).padStart(2, '0')
  if (format === 'hm') return `${hh}:${mm}`
  const ss = String(d.getUTCSeconds()).padStart(2, '0')
  return `${hh}:${mm}:${ss}`
}

export function initials(name: string): string {
  const cleaned = name.replace(/[^a-zA-Z0-9 _-]/g, '').trim()
  if (!cleaned) return '?'
  const parts = cleaned.split(/[\s_-]+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return cleaned.slice(0, 2).toUpperCase()
}